import { FC } from 'react';

import { BannerModerationStatus } from 'utils/constants/banner';
import { CampaignSource, CampaignSubSource, DISABLE_OFF_SOURCES, HH_SPLIT_SUB_SOURCES } from 'utils/constants/campaign';
import copyReason from 'utils/copy';

import { VSpacingContainer } from '@hh.ru/magritte-ui';

import Tip, { TipPlacement } from 'bloko/blocks/drop/Tip';
import HoverTip from 'bloko/blocks/drop/Tip/HoverTip';
import HSpacing from 'bloko/blocks/hSpacing';
import Icon, { IconAppearance, IconColor, IconKind, IconScale, IconView } from 'bloko/blocks/icon';
import Link, { LinkAppearance, LinkKind } from 'bloko/blocks/link';
import Text from 'bloko/blocks/text';

import useTrls from 'hooks/translation/useTrls';

import { SessionType } from 'store/models/user';
import { useSelector } from 'store/utils';

import styles from 'components/Banner/Actions/Social/Social.module.less';

// временное решение для того, чтобы не выключать площадки у пользователей
const DISABLE_OFF_SOURCES_REASONS = {
    [CampaignSubSource.Facebook]: 'К\u00A0сожалению, Facebook закрыл возможность размещения рекламы',
    [CampaignSubSource.Instagram]: 'К\u00A0сожалению, Instagram закрыл возможность размещения рекламы',
};

interface SocialProps {
    moderationReason: string;
    source: CampaignSource;
    status: BannerModerationStatus;
    subSources?: CampaignSubSource[];
    bannedAsAdvertisement: boolean;
    bannedForAllSources: boolean;
}

export const Social: FC<SocialProps> = ({
    source,
    subSources = [],
    status,
    moderationReason,
    bannedAsAdvertisement,
    bannedForAllSources,
}) => {
    const sessionType = useSelector((state) => state.user.sessionType);

    const trl = useTrls();

    // exp_PORTFOLIO-53635_start
    const hasSplitHHSubSources =
        source === CampaignSource.HH && subSources.some((s) => HH_SPLIT_SUB_SOURCES.includes(s));
    const isVKSourceOrHHSplitSource = source === CampaignSource.VKAds || hasSplitHHSubSources;

    const trlSource = isVKSourceOrHHSplitSource ? source : subSources[0];
    // exp_PORTFOLIO-53635_end

    if (DISABLE_OFF_SOURCES.includes(subSources[0]) && status === BannerModerationStatus.New) {
        status = BannerModerationStatus.Banned;
        moderationReason = DISABLE_OFF_SOURCES_REASONS[subSources[0] as keyof typeof DISABLE_OFF_SOURCES_REASONS];
    }

    const textsTip = {
        [BannerModerationStatus.Banned]: (reason: string) => reason || trl('banner.actions.social.textTip.banned'),
        [BannerModerationStatus.New]: () => trl('banner.actions.social.textTip.new'),
        [BannerModerationStatus.Moderated]: () => trl('banner.actions.social.textTip.moderated'),
    };

    const renderText = textsTip.hasOwnProperty(status) ? textsTip[status] : () => '';

    const Icons = {
        [BannerModerationStatus.Banned]: (
            <Icon view={IconView.Ban} initial={IconColor.SpecialRedBranded} scale={IconScale.Small} />
        ),
        [BannerModerationStatus.New]: (
            <Icon view={IconView.Clock} initial={IconColor.Gray50} kind={IconKind.Default} scale={IconScale.Small} />
        ),
        [BannerModerationStatus.Moderated]: (
            <Icon
                view={IconView.Checkmark}
                initial={IconColor.Green60}
                kind={IconKind.Single}
                appearance={IconAppearance.Outlined}
                scale={IconScale.Small}
            />
        ),
    };

    const content = (
        <span>
            {Icons.hasOwnProperty(status) && Icons[status]}
            <span className={styles.bannerStatusSourceText}>
                {status === BannerModerationStatus.Banned &&
                subSources &&
                !(bannedAsAdvertisement || bannedForAllSources) ? (
                    <Link
                        appearance={LinkAppearance.Pseudo}
                        kind={LinkKind.Tertiary}
                        onClick={() => {
                            if (sessionType === SessionType.Sudo) {
                                copyReason(moderationReason);
                            }
                        }}
                    >
                        {trl(`banner.actions.social.source.${trlSource}`)}
                    </Link>
                ) : (
                    trl(`banner.actions.social.source.${trlSource}`)
                )}
            </span>
        </span>
    );

    return (
        <div className={styles.bannerStatusSource}>
            {bannedAsAdvertisement || bannedForAllSources ? (
                content
            ) : (
                <HoverTip
                    placement={TipPlacement.Top}
                    render={() =>
                        moderationReason ? (
                            <VSpacingContainer default={8}>
                                <Text strong>{trl('banner.actions.social.modal.title')}</Text>
                                {moderationReason.split('\n').map((item, key) => (
                                    <Text key={key}>{item}</Text>
                                ))}
                            </VSpacingContainer>
                        ) : (
                            renderText(moderationReason)
                        )
                    }
                    host={document.body}
                    DropElement={Tip}
                >
                    {content}
                </HoverTip>
            )}
            {/* exp_PORTFOLIO-53635_next_line */}
            {isVKSourceOrHHSplitSource && (
                <>
                    <HSpacing base={1} />
                    <HoverTip
                        placement={TipPlacement.Top}
                        render={() => (
                            <div className={styles.bannerStatusSourceTip}>
                                <span>{trl('Statistic.Header.tip.title')}</span>
                                <ul className={styles.bannerStatusSourceTipUl}>
                                    {subSources.map((subSourcesItem, key) => (
                                        <li key={key}>{trl(`banner.actions.social.source.${subSourcesItem}`)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    >
                        <Icon view={IconView.Info} initial={IconColor.Gray40} scale={IconScale.Small} />
                    </HoverTip>
                </>
            )}
        </div>
    );
};
